/**
 * Material Renderer Component
 *
 * Main router component that delegates to type-specific material renderers.
 * Handles all material types: video, image, pdf, doc, audio, other.
 */

import { useTranslation } from 'react-i18next';
import { getLocalizedTitle, type Material } from '@immopersian/common';
import { VideoMaterial } from './materials/VideoMaterial';
import { ImageMaterial } from './materials/ImageMaterial';
import { PdfMaterial } from './materials/PdfMaterial';
import { DocMaterial } from './materials/DocMaterial';
import { AudioMaterial } from './materials/AudioMaterial';
import { OtherMaterial } from './materials/OtherMaterial';

interface MaterialRendererProps {
  material: Material;
  sessionId?: string;
}

/**
 * Renders a material based on its type.
 *
 * Supports:
 * - Video: Cloudflare Stream or Mux with analytics
 * - Image: Display with download protection
 * - PDF: Document viewer with download
 * - Doc: Text documents with download
 * - Audio: HTML5 audio player
 * - Other: Generic file download
 */
export function MaterialRenderer({ material, sessionId }: MaterialRendererProps) {
  const { i18n } = useTranslation();

  return (
    <div className="space-y-4">
      {/* Material Title */}
      <h3 className="text-xl sm:text-2xl font-semibold text-brand-dark">
        {getLocalizedTitle(material, i18n.language)}
      </h3>

      {/* Delegate to type-specific renderer */}
      {material.type === 'video' && <VideoMaterial material={material} sessionId={sessionId} />}
      {material.type === 'image' && <ImageMaterial material={material} />}
      {material.type === 'pdf' && <PdfMaterial material={material} />}
      {material.type === 'doc' && <DocMaterial material={material} />}
      {material.type === 'audio' && <AudioMaterial material={material} />}
      {material.type === 'other' && <OtherMaterial material={material} />}
    </div>
  );
}
